import React, {
  useState,
  createContext,
  useEffect,
  useCallback,
  useContext,
} from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useLocation } from "react-router-dom";
import PropTypes from "prop-types";
import Video from "twilio-video";
import { RoomContext } from "./RoomContext"; // adjust path if needed

export const MediaContext = createContext({
  mediaStream: null,
  localAudioTrack: null,
  localVideoTrack: null,
  audioEnabled: true,
  videoEnabled: true,
  setAudioEnabled: () => {},
  setVideoEnabled: () => {},
  releaseMedia: () => {},
  mediaError: null,
});

export const MediaProvider = ({ children }) => {
  const [mediaStream, setMediaStream] = useState(null);
  const [localAudioTrack, setLocalAudioTrack] = useState(null);
  const [localVideoTrack, setLocalVideoTrack] = useState(null);

  const [audioEnabled, setAudioEnabledState] = useState(true);
  const [videoEnabled, setVideoEnabledState] = useState(true);

  const [retryCount, setRetryCount] = useState(0);
  const [mediaError, setMediaError] = useState(null);

  const { isAuthenticated } = useAuth0();
  const hasPasswordlessToken = localStorage.getItem("vibeu_access_token");
  const location = useLocation();

  const { room } = useContext(RoomContext);
  const inRoom = Boolean(room);

  const allowMediaAccess =
    (isAuthenticated || hasPasswordlessToken) &&
    location.pathname === "/dashboard";

  const MAX_RETRIES = 20;
  const RETRY_DELAY_MS = 5000;

  const stopAndClearTwilioTrack = (t) => {
    if (!t) return;
    try {
      // Twilio LocalTrack has stop()
      t.stop();
    } catch (e) {
      console.error("err", e);
    }
  };

  const stopMediaStreamTracks = (stream) => {
    if (!stream) return;
    stream.getTracks().forEach((t) => {
      try {
        t.stop();
      } catch (e) {
        console.error("er", e);
      }
    });
  };

  const releaseMedia = useCallback(() => {
    stopAndClearTwilioTrack(localVideoTrack);
    stopAndClearTwilioTrack(localAudioTrack);
    stopMediaStreamTracks(mediaStream);

    setLocalVideoTrack(null);
    setLocalAudioTrack(null);
    setMediaStream(null);
  }, [localVideoTrack, localAudioTrack, mediaStream]);

  const buildCombinedStream = useCallback((audioTrack, videoTrack) => {
    const tracks = [];
    if (audioTrack?.mediaStreamTrack) tracks.push(audioTrack.mediaStreamTrack);
    if (videoTrack?.mediaStreamTrack) tracks.push(videoTrack.mediaStreamTrack);
    return tracks.length ? new MediaStream(tracks) : null;
  }, []);

  // AUDIO toggle: always soft (never hard-stop mic)
  const setAudioEnabled = useCallback(
    (enabled) => {
      setAudioEnabledState(enabled);

      // Twilio LocalAudioTrack soft toggle
      if (localAudioTrack) {
        try {
          enabled ? localAudioTrack.enable() : localAudioTrack.disable();
        } catch (e) {
          console.error("er", e);
        }
      }

      // also keep mediaStream track enabled flag aligned (harmless)
      if (mediaStream) {
        mediaStream.getAudioTracks().forEach((t) => {
          t.enabled = enabled;
        });
      }
    },
    [localAudioTrack, mediaStream]
  );

  // VIDEO toggle:
  // - inRoom: SOFT toggle only (do not stop / replace track)
  // - not inRoom: allow hard release + clean recreate
  const setVideoEnabled = useCallback(
    async (enabled) => {
      if (enabled === videoEnabled) return;

      // ✅ IN ROOM: soft toggle only
      if (inRoom) {
        setVideoEnabledState(enabled);
        if (localVideoTrack) {
          try {
            enabled ? localVideoTrack.enable() : localVideoTrack.disable();
          } catch (e) {
            console.error("err", e);
          }
        }
        if (mediaStream) {
          mediaStream.getVideoTracks().forEach((t) => {
            t.enabled = enabled;
          });
        }
        return;
      }

      // ✅ NOT IN ROOM: hard OFF releases camera cleanly
      if (!enabled) {
        setVideoEnabledState(false);

        if (localVideoTrack) stopAndClearTwilioTrack(localVideoTrack);
        setLocalVideoTrack(null);

        // rebuild stream with audio only
        const nextStream = buildCombinedStream(localAudioTrack, null);
        setMediaStream(nextStream);

        return;
      }

      // ✅ NOT IN ROOM: hard ON reacquires camera + creates NEW LocalVideoTrack
      try {
        const s = await navigator.mediaDevices.getUserMedia({
          video: true,
          audio: false,
        });

        const msTrack = s.getVideoTracks()[0];
        if (!msTrack) throw new Error("No camera track returned");

        const nextLocalVideo = new Video.LocalVideoTrack(msTrack);

        // Apply state immediately
        try {
          nextLocalVideo.enable();
        } catch (e) {
          console.error("err", e);
        }

        setLocalVideoTrack(nextLocalVideo);
        setVideoEnabledState(true);

        // rebuild stream with audio + new video
        const nextStream = buildCombinedStream(localAudioTrack, nextLocalVideo);
        setMediaStream(nextStream);

        setMediaError(null);
      } catch (e) {
        console.error("[Media] enable video failed:", e);
        setMediaError(e);
      }
    },
    [
      inRoom,
      videoEnabled,
      localVideoTrack,
      localAudioTrack,
      mediaStream,
      buildCombinedStream,
    ]
  );

  // Initial acquire on dashboard: create Twilio LocalTracks once
  useEffect(() => {
    let mounted = true;
    let retryTimer = null;

    const acquire = async () => {
      if (!allowMediaAccess) {
        // leaving dashboard: only release if NOT in room
        if (!inRoom) releaseMedia();
        return;
      }

      // already have what we need (for preview and/or publishing)
      if (localAudioTrack || localVideoTrack) return;

      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: true,
          video: true,
        });

        if (!mounted) {
          stopMediaStreamTracks(stream);
          return;
        }

        const msAudio = stream.getAudioTracks()[0] || null;
        const msVideo = stream.getVideoTracks()[0] || null;

        const tAudio = msAudio ? new Video.LocalAudioTrack(msAudio) : null;
        const tVideo = msVideo ? new Video.LocalVideoTrack(msVideo) : null;

        // apply current flags
        if (tAudio) {
          audioEnabled ? tAudio.enable() : tAudio.disable();
        }
        if (tVideo) {
          videoEnabled ? tVideo.enable() : tVideo.disable();
        }

        setLocalAudioTrack(tAudio);
        setLocalVideoTrack(tVideo);
        setMediaStream(buildCombinedStream(tAudio, tVideo));

        setRetryCount(0);
        setMediaError(null);
      } catch (e) {
        if (!mounted) return;
        setMediaError(e);

        if (retryCount < MAX_RETRIES) {
          retryTimer = setTimeout(
            () => setRetryCount((p) => p + 1),
            RETRY_DELAY_MS
          );
        }
      }
    };

    acquire();

    return () => {
      mounted = false;
      if (retryTimer) clearTimeout(retryTimer);
    };
  }, [
    allowMediaAccess,
    inRoom,
    releaseMedia,
    retryCount,
    localAudioTrack,
    localVideoTrack,
    audioEnabled,
    videoEnabled,
    buildCombinedStream,
  ]);

  // ✅ Reconcile: if videoEnabled is true but we have no localVideoTrack (dashboard, not in-room), reacquire
  useEffect(() => {
    if (!allowMediaAccess) return;
    if (inRoom) return;

    if (videoEnabled && !localVideoTrack) {
      // fire-and-forget; function handles errors
      setVideoEnabled(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowMediaAccess, inRoom, videoEnabled, localVideoTrack]);

  return (
    <MediaContext.Provider
      value={{
        mediaStream,
        localAudioTrack,
        localVideoTrack,
        audioEnabled,
        videoEnabled,
        setAudioEnabled,
        setVideoEnabled,
        releaseMedia,
        mediaError,
      }}
    >
      {children}
    </MediaContext.Provider>
  );
};

MediaProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
